import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mapPlacePredictionToLegacy,
  toIncludedRegionCodes,
  toLegacyAddressComponents
} from './googleMapsPlacesMapping';

/**
 * Hook de integración con Google Maps JS (Places New, Geocoder).
 * Las predicciones de autocomplete y el detalle por `placeId` vía cliente Google están deprecadas
 * en favor de `useGoogleMapsPlacesBff` (BFF publication-api). Aquí se mantienen `geocode` y carga
 * de librerías usadas por el hook compuesto. Restricción de tipos `address` / `includedPrimaryTypes`:
 * ver AVISOS-6014 (no reenviar tipos; el BFF no debe enviar `includedPrimaryTypes` salvo acuerdo explícito).
 *
 * Los helpers puros de mapeo viven en `./googleMapsPlacesMapping` para poder testearlos
 * sin arrastrar React / window.google.
 */

/**
 * Campos para Place.fetchFields (Places API New).
 */
const DEFAULT_FIELDS = ['id', 'formattedAddress', 'location', 'addressComponents', 'shortFormattedAddress'];

const isMapsReady = () => typeof window !== 'undefined' && !!window.google?.maps;

const DEFAULT_DEBOUNCE_MS = 350;

/**
 * Carga la librería places (New) vía importLibrary.
 * Requiere el bootstrap loader (useGoogleMapsBootstrap).
 * @returns {Promise<{AutocompleteSuggestion, AutocompleteSessionToken, Place}>}
 */
async function loadPlacesLibrary() {
  if (!isMapsReady()) {
    throw new Error('Google Maps no está cargado. Usa useGoogleMapsBootstrap.');
  }
  if (typeof window.google.maps.importLibrary !== 'function') {
    throw new Error('importLibrary no disponible. Usa useGoogleMapsBootstrap en lugar de useLoadScript.');
  }
  const lib = await window.google.maps.importLibrary('places');
  if (!lib?.AutocompleteSuggestion) {
    throw new Error('Places API (New) no disponible.');
  }
  return {
    AutocompleteSuggestion: lib.AutocompleteSuggestion,
    AutocompleteSessionToken: lib.AutocompleteSessionToken,
    Place: lib.Place
  };
}

export const useGoogleMapsAPI = ({
  componentRestrictions,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  isScriptLoaded = true
} = {}) => {
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState(null);

  const placesLibRef = useRef(null);
  const geocoderRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const initPlacesLib = useCallback(async () => {
    if (!isScriptLoaded) return null;
    if (placesLibRef.current) return placesLibRef.current;
    try {
      placesLibRef.current = await loadPlacesLibrary();
      setReady(true);
      return placesLibRef.current;
    } catch {
      return null;
    }
  }, [isScriptLoaded]);

  useEffect(() => {
    if (isScriptLoaded) {
      initPlacesLib();
    }
  }, [initPlacesLib, isScriptLoaded]);

  const ensureSessionToken = useCallback(() => {
    const lib = placesLibRef.current;
    if (!lib?.AutocompleteSessionToken) return undefined;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSession = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  /**
   * @deprecated Autocomplete directo contra Google Places. El flujo soportado usa
   * `useGoogleMapsPlacesBff` (BFF `realestate-posting-publication-api`). Se conserva por compatibilidad temporal.
   */
  const searchImmediate = useCallback(
    async input => {
      const lib = await initPlacesLib();
      if (!lib?.AutocompleteSuggestion) {
        setPredictions([]);
        return { predictions: [], status: 'NOT_READY' };
      }

      setLoading(true);
      setError(null);

      const request = {
        input,
        sessionToken: ensureSessionToken(),
        includedRegionCodes: toIncludedRegionCodes(componentRestrictions)
      };

      try {
        const { suggestions } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        const legacyPredictions = (suggestions ?? [])
          .map(s => (s.placePrediction ? mapPlacePredictionToLegacy(s.placePrediction) : null))
          .filter(Boolean);
        setPredictions(legacyPredictions);
        setLoading(false);
        const status = legacyPredictions.length === 0 ? 'ZERO_RESULTS' : 'OK';
        return { predictions: legacyPredictions, status };
      } catch (err) {
        setLoading(false);
        setPredictions([]);
        const code = err?.code ?? err?.status;
        const isZeroResults =
          code === 'ZERO_RESULTS' ||
          code === 'NOT_FOUND' ||
          (typeof code === 'string' && code.toUpperCase().includes('ZERO_RESULTS')) ||
          (typeof err?.message === 'string' && err.message.toLowerCase().includes('zero'));
        const status = isZeroResults ? 'ZERO_RESULTS' : code || 'UNKNOWN_ERROR';
        if (!isZeroResults) {
          setError(status);
        }
        return { predictions: [], status };
      }
    },
    [componentRestrictions, ensureSessionToken, initPlacesLib]
  );

  /**
   * @deprecated Ver `searchImmediate`. Usar `useGoogleMapsPlacesBff` para predicciones en UI.
   */
  const search = useCallback(
    input => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const trimmedInput = input?.trim();
      if (!trimmedInput) {
        setPredictions([]);
        setError(null);
        return Promise.resolve({ predictions: [], status: 'EMPTY' });
      }
      return new Promise(resolve => {
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          searchImmediate(trimmedInput).then(resolve);
        }, debounceMs);
      });
    },
    [debounceMs, searchImmediate]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * @deprecated Detalle directo contra Google Places (`Place.fetchFields`). El flujo soportado usa
   * `useGoogleMapsPlacesBff`. Se conserva por compatibilidad temporal.
   */
  const getDetails = useCallback(
    async placeId => {
      const lib = await initPlacesLib();
      if (!lib?.Place) {
        setError('NOT_READY');
        throw new Error('Google Places no está listo.');
      }
      if (!placeId) {
        setError('INVALID_PLACE_ID');
        throw new Error('placeId inválido.');
      }

      setLoading(true);
      setError(null);

      try {
        const place = new lib.Place({ id: placeId });
        await place.fetchFields({ fields: DEFAULT_FIELDS });

        const lat = place.location?.lat?.() ?? null;
        const lng = place.location?.lng?.() ?? null;

        const addressComponents = place.addressComponents ?? [];
        const legacyAddressComponents = toLegacyAddressComponents(addressComponents);
        const raw = {
          place_id: place.id ?? placeId,
          formatted_address: place.formattedAddress ?? '',
          name: place.shortFormattedAddress ?? '',
          geometry: place.location
            ? {
                location: {
                  lat: () => place.location.lat(),
                  lng: () => place.location.lng()
                }
              }
            : null,
          address_components: legacyAddressComponents
        };

        setLoading(false);
        return {
          placeId: place.id ?? placeId,
          address: place.formattedAddress ?? '',
          name: place.shortFormattedAddress ?? '',
          lat,
          lng,
          raw
        };
      } catch (err) {
        setLoading(false);
        setError(err?.status || 'UNKNOWN_ERROR');
        throw new Error(err?.message || err?.status || 'Error al obtener detalles.');
      }
    },
    [initPlacesLib]
  );

  /**
   * Geocodifica una dirección completa sin usar autocomplete.
   * Usa Geocoder para direcciones escritas manualmente cuando el usuario NO selecciona de las sugerencias.
   * Si la dirección contiene "Otros Paises", no aplica restricciones de país para permitir búsqueda global.
   */
  const geocode = useCallback((address, options = {}) => {
    return new Promise((resolve, reject) => {
      if (!geocoderRef.current) {
        if (isMapsReady()) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }
        if (!geocoderRef.current) {
          reject(new Error('Google Geocoder no está listo.'));
          return;
        }
      }
      const OTROS_PAISES_STRINGS = ['Otros Paises', 'Outros Paises'];
      const hasOtrosPaises = typeof address === 'string' && OTROS_PAISES_STRINGS.some(s => address.includes(s));
      const effectiveOptions = hasOtrosPaises ? {} : options;
      const request = { address, ...effectiveOptions };
      geocoderRef.current.geocode(request, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && Array.isArray(results)) {
          resolve({ results, status });
          return;
        }
        resolve({ results: [], status });
      });
    });
  }, []);

  return {
    ready,
    loading,
    predictions,
    search,
    getDetails,
    geocode,
    resetSession,
    error
  };
};
